import { useState, ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { 
  List,
  X,
  ChartBar,
  Users,
  CreditCard,
  Ticket,
  ChartLineUp,
  Gear,
  House,
  SignOut,
  Bell,
  UserCircle,
  Crown,
  ChatCircle,
  Trophy
} from 'phosphor-react'
import { useAuth } from '../hooks/useAuth'

interface LayoutProps {
  children: ReactNode
}

interface TooltipProps {
  children: ReactNode
  content: string
}

const Tooltip = ({ children, content }: TooltipProps) => {
  return (
    <div className="group relative">
      {children}
      <div className="absolute left-full ml-2 top-1/2 transform -translate-y-1/2 bg-gray-900 text-white text-sm px-2 py-1 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
        {content}
        <div className="absolute top-1/2 left-0 transform -translate-y-1/2 -translate-x-full border-4 border-transparent border-r-gray-900"></div>
      </div>
    </div>
  )
}

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: House },
  { name: 'Usuários', href: '/users', icon: Users },
  { name: 'Assinaturas', href: '/subscriptions', icon: CreditCard },
  { name: 'Cupons', href: '/coupons', icon: Ticket },
  { name: 'Contas Bonificadas', href: '/bonus-accounts', icon: Crown },
  { name: 'Desafios', href: '/challenges', icon: Trophy },
  { name: 'Mensageria', href: '/messaging', icon: ChatCircle },
  { name: 'Relatórios', href: '/reports', icon: ChartLineUp },
  { name: 'Configurações', href: '/settings', icon: Gear },
]

export default function Layout({ children }: LayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const location = useLocation()
  const { admin, logout } = useAuth()

  const isActive = (path: string) => {
    return location.pathname === path
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar */}
      <div className={`lg:hidden ${sidebarOpen ? 'fixed inset-0 z-50' : ''}`}>
        <div className={`fixed inset-0 bg-gray-600 bg-opacity-75 transition-opacity duration-300 ${sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} 
             onClick={() => setSidebarOpen(false)} />
        
        <div className={`fixed inset-y-0 left-0 flex w-64 flex-col shadow-xl transform transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`} style={{backgroundColor: '#245864'}}>
          <div className="flex items-center justify-between p-4 border-b border-white border-opacity-20">
            <div className="flex items-center">
              <ChartBar size={32} className="text-white" />
              <span className="ml-2 text-xl font-bold text-white">Snapness</span>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-2 text-white opacity-70 hover:opacity-100 transition-opacity duration-200"
            >
              <X size={24} />
            </button>
          </div>
          
          <nav className="flex-1 px-4 py-6 space-y-2">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`group flex items-center px-4 py-3 text-sm font-medium rounded-md transition-all duration-200 ${
                  isActive(item.href)
                    ? 'bg-white bg-opacity-20 text-white'
                    : 'text-white opacity-70 hover:opacity-100 hover:bg-white hover:bg-opacity-10'
                }`}
                onClick={() => setSidebarOpen(false)}
              >
                <item.icon size={24} className="mr-3" />
                {item.name}
              </Link>
            ))}
          </nav>
          
          <div className="p-4 border-t border-white border-opacity-20">
            <div className="flex items-center mb-4">
              <UserCircle size={40} className="text-white opacity-70" />
              <div className="ml-3">
                <p className="text-sm font-medium text-white">{admin?.name}</p>
                <p className="text-xs text-white opacity-70">{admin?.email}</p>
              </div>
            </div>
            <button
              onClick={logout}
              className="w-full flex items-center px-2 py-2 text-sm text-white opacity-70 hover:opacity-100 hover:bg-white hover:bg-opacity-10 rounded-md transition-all duration-200"
            >
              <SignOut size={20} className="mr-3" />
              Sair
            </button>
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-20 lg:flex-col">
        <div className="flex flex-col flex-1 shadow-lg" style={{backgroundColor: '#245864'}}>
          <div className="flex items-center justify-center p-4 border-b border-white border-opacity-20">
            <ChartBar size={32} className="text-white" />
          </div>
          
          <nav className="flex-1 px-2 py-6 space-y-2">
            {navigation.map((item) => (
              <Tooltip key={item.name} content={item.name}>
                <Link
                  to={item.href}
                  className={`group flex items-center justify-center p-3 text-sm font-medium rounded-md transition-all duration-200 ${
                    isActive(item.href)
                      ? 'bg-white bg-opacity-20 text-white'
                      : 'text-white opacity-70 hover:opacity-100 hover:bg-white hover:bg-opacity-10'
                  }`}
                >
                  <item.icon size={24} />
                </Link>
              </Tooltip>
            ))}
          </nav>
          
          <div className="p-2 border-t border-white border-opacity-20">
            <Tooltip content="Configurações do perfil">
              <div className="flex items-center justify-center mb-4">
                <UserCircle size={32} className="text-white opacity-70" />
              </div>
            </Tooltip>
            <Tooltip content="Sair do sistema">
              <button
                onClick={logout}
                className="w-full flex items-center justify-center p-3 text-white opacity-70 hover:opacity-100 hover:bg-white hover:bg-opacity-10 rounded-md transition-all duration-200"
              >
                <SignOut size={20} />
              </button>
            </Tooltip>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:ml-20">
        {/* Top bar */}
        <div className="bg-white shadow-sm border-b">
          <div className="flex items-center justify-between px-4 py-4">
            <div className="flex items-center">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 text-gray-500 hover:text-gray-700"
              >
                <List size={24} />
              </button>
              
              <h1 className="text-xl font-semibold text-gray-900 ml-4 lg:ml-0">
                {navigation.find(item => item.href === location.pathname)?.name || 'Dashboard'}
              </h1>
            </div>
            
            <div className="flex items-center space-x-4">
              <button className="p-2 text-gray-500 hover:text-gray-700">
                <Bell size={24} />
              </button>
              
              <div className="flex items-center space-x-2">
                <UserCircle size={32} className="text-gray-400" />
                <div className="hidden sm:block">
                  <p className="text-sm font-medium text-gray-900">{admin?.name}</p>
                  <p className="text-xs text-gray-500 capitalize">{admin?.role}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="p-6">
          <div className="max-w-7xl mx-auto">
            {children}
          </div>
        </main>
      </div>
    </div>
  )
} 